import React from 'react';
import { createPortal } from 'react-dom';

// ---------- formatting ----------
export function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function timeAgo(value) {
  if (!value) return '—';
  const d = new Date(value).getTime();
  if (Number.isNaN(d)) return '—';
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24); if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7); if (weeks < 5) return `${weeks}w ago`;
  return fmtDate(value);
}

// Forward countdown for an empty "ghost" moment slated for auto-close (backend `autoCloseAt`).
// Mirrors the Flutter chip copy. Returns e.g. "Closes in 2h" / "Closing soon" (under ~2 min).
export function closesInLabel(value) {
  if (!value) return null;
  const ms = new Date(value).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 2 * 60 * 1000) return 'Closing soon';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `Closes in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem >= 5 ? `Closes in ${hrs}h ${rem}m` : `Closes in ${hrs}h`;
}

// Hue seeded from a name — mirrors the app's name-seeded circle emblems (190–285°).
export function hueFromString(str) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
  return 190 + (h % 95);
}

// Location can be a plain string OR a structured place object
// ({ name, address, latitude, longitude, radiusMeters }). Always render a string.
export function fmtLocation(loc) {
  if (!loc) return null;
  if (typeof loc === 'string') return loc;
  if (typeof loc === 'object') {
    if (loc.name || loc.address) return loc.name || loc.address;
    // Coords-only (e.g. "Use my location" pin with no typed address) — show the actual
    // shared coordinates, which any device can route to, not a device-relative label.
    if (loc.latitude != null && loc.longitude != null) {
      return `📍 ${Number(loc.latitude).toFixed(5)}, ${Number(loc.longitude).toFixed(5)}`;
    }
    return null;
  }
  return null;
}

// Google Maps directions URL to a moment/event location, or null if there's nothing to
// route to. Prefers exact coordinates (most precise) and falls back to the address/name.
// `dir/?api=1&destination=...` opens turn-by-turn from the user's current location.
export function mapsDirectionsUrl(loc) {
  if (!loc) return null;
  let dest = null;
  if (typeof loc === 'string') {
    dest = loc;
  } else if (typeof loc === 'object') {
    if (loc.latitude != null && loc.longitude != null) dest = `${loc.latitude},${loc.longitude}`;
    else dest = loc.address || loc.name || null;
  }
  if (!dest) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

// Human duration between two timestamps (e.g. "1h 25m"). null if not computable.
export function durationLabel(startVal, endVal) {
  if (!startVal || !endVal) return null;
  const ms = new Date(endVal).getTime() - new Date(startVal).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function durationMinutes(startVal, endVal) {
  if (!startVal || !endVal) return null;
  const ms = new Date(endVal).getTime() - new Date(startVal).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.round(ms / 60000);
}

// ---------- avatar ----------
export function Avatar({ src, name, emoji, size = 44, node = false }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const inner = src
    ? <img className="db-avatar" src={src} alt="" style={{ width: size, height: size }} />
    : emoji
      ? <span className="db-avatar" style={{ width: size, height: size, fontSize: size * 0.5 }}>{emoji}</span>
      : <span className="db-avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.42 }}>{initial}</span>;
  if (!node) return inner;
  return <span className="db-avatar-node">{inner}</span>;
}

// ---------- stat card ----------
// Label on top, value beneath — the single shared stat-card format (matches the Insights tiles).
// Use everywhere a "title + value" card is shown so they're all consistent.
export function StatCard({ label, value, sub }) {
  return (
    <div className="db-card">
      <div className="db-stat db-stat-flip"><span>{label}</span><b>{value}</b></div>
      {sub && <div className="db-card-meta">{sub}</div>}
    </div>
  );
}

// ---------- circle emblem (mini gyrosphere) ----------
// A name-seeded mini "gyrosphere": a shaded sphere wrapped by a single tilted orbital ring with a
// friend-dot riding it — echoing the brand SphereOrb (login/cold-start hero) instead of the old
// flat orb with a stray inner ellipse. The hue still seeds identity so every circle reads distinct.
export function CircleEmblem({ name, size = 44 }) {
  const hue = hueFromString(name);
  const gid = `ce${React.useId().replace(/:/g, '')}`; // unique, colon-free gradient id
  const light = `hsl(${hue} 92% 72%)`;
  const mid = `hsl(${hue} 82% 50%)`;
  const deep = `hsl(${(hue + 38) % 360} 72% 28%)`;
  const ring = `hsl(${hue} 96% 84%)`;
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" aria-hidden="true" style={{ flex: 'none', display: 'block' }}>
      <defs>
        <radialGradient id={gid} cx="37%" cy="31%" r="78%">
          <stop offset="0%" stopColor={light} />
          <stop offset="55%" stopColor={mid} />
          <stop offset="100%" stopColor={deep} />
        </radialGradient>
      </defs>
      {/* the sphere */}
      <circle cx="22" cy="22" r="19" fill={`url(#${gid})`} />
      {/* single tilted orbital ring + a friend-dot riding it (the "gyro") */}
      <g transform="rotate(-24 22 22)">
        <ellipse cx="22" cy="22" rx="19" ry="6.8" fill="none" stroke={ring} strokeOpacity="0.9" strokeWidth="1.4" />
        <circle cx="3.2" cy="22" r="1.9" fill="#fff" />
      </g>
      {/* crisp rim + soft specular */}
      <circle cx="22" cy="22" r="19" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
      <ellipse cx="15" cy="13" rx="6" ry="3.4" fill="rgba(255,255,255,0.22)" transform="rotate(-24 15 13)" />
    </svg>
  );
}

// ---------- status chip ----------
export function StatusChip({ status, live }) {
  if (live || status === 'in_progress' || status === 'started') return <span className="db-chip db-chip-live">● Live</span>;
  if (status === 'planned') return <span className="db-chip db-chip-planned">Planned</span>;
  if (status === 'cancelled' || status === 'auto_closed_empty') return <span className="db-chip db-chip-closed">Closed</span>;
  return <span className="db-chip db-chip-done">Done</span>;
}

// ---------- async states ----------
export function Loading() {
  return <div className="db-loading"><span className="db-spinner" /></div>;
}

export function ErrorBox({ error }) {
  const msg = error === 'not_crew_member' || error === 'friend_not_found' || error === 'moment_not_found'
    ? 'This isn’t available to you, or no longer exists.'
    : `Couldn’t load this (${error}).`;
  return <div className="db-error">{msg}</div>;
}

export function Empty({ children }) {
  return <div className="db-empty">{children}</div>;
}

// Unread indicators for moment cards — message + media icons matching the mobile app
// (amber chat bubble, purple photo). Render only when there's unseen content.
export function UnreadMsgIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="#f59e0b" aria-hidden="true"
      style={{ verticalAlign: '-2px' }}>
      <title>New messages</title>
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
    </svg>
  );
}

export function UnreadMediaIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="#a855f7" aria-hidden="true"
      style={{ verticalAlign: '-2px' }}>
      <title>New photos or videos</title>
      <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
    </svg>
  );
}

// "Everyone's going!" celebration badge — parity with the app. Shown on a planned moment once every
// circle member has RSVP'd "going". Bright green so it reads as a happy, unanimous all-in signal.
export function EveryoneGoingBadge({ count }) {
  return (
    <span
      title="Everyone is going"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
        borderRadius: 999, fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.4,
        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
        boxShadow: '0 2px 8px rgba(34,197,94,0.35)'
      }}
    >
      🎉 Everyone&apos;s going!{count != null ? ` · ${count}` : ''}
    </span>
  );
}

// Compact "N going" headcount pill for planned moment cards — green so the plan's momentum reads
// at a glance. Hidden by callers once everyone's in (EveryoneGoingBadge covers that case).
export function GoingCount({ count }) {
  if (!count) return null;
  return (
    <span
      title={`${count} going`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px',
        borderRadius: 999, fontSize: 12, fontWeight: 700, lineHeight: 1.4,
        color: '#16a34a', background: 'rgba(34,197,94,0.16)'
      }}
    >
      👥 {count} going
    </span>
  );
}

// Wave-3 sparkles — a burst of colorful sparks that fly out of the card and fade. Pure CSS;
// deterministic-ish per render. Render inside a positioned (.db-card-rave-3 / .db-panel-rave-3) box.
export function RaveSparkles() {
  const sparks = React.useMemo(() => Array.from({ length: 16 }, (_, i) => {
    const a = (i / 16) * Math.PI * 2 + Math.random() * 0.6;
    const dist = 48 + Math.random() * 44;
    const hue = Math.floor(Math.random() * 360);
    return {
      dx: Math.cos(a) * dist,
      dy: Math.sin(a) * dist,
      left: 25 + Math.random() * 50,
      top: 25 + Math.random() * 50,
      delay: Math.random() * 1.8,
      hue,
    };
  }), []);
  return (
    <span className="db-rave-sparkles" aria-hidden="true">
      {sparks.map((s, i) => (
        <span
          key={i}
          className="db-spark"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            color: `hsl(${s.hue} 100% 60%)`,
            background: `hsl(${s.hue} 100% 60%)`,
            '--dx': `${s.dx}px`,
            '--dy': `${s.dy}px`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </span>
  );
}

// Maps a rave wave (1/2/3) to its card/panel CSS class suffix. Empty when not raving.
export function raveWaveClass(prefix, wave) {
  return wave >= 1 && wave <= 3 ? ` ${prefix}-rave-${wave}` : '';
}

// A child's real avatar — their photo/medallion if set, else their chosen emoji. Mirrors what the
// child shows elsewhere so the parent's Family view matches the child's own profile.
export function ChildAvatar({ child, size = 40 }) {
  // Reuse the canonical Avatar (handles the badge image src + emoji + name-initial fallback) so the
  // child's avatar matches exactly what they show elsewhere in the app.
  return (
    <Avatar
      src={child?.avatarImageUrl || child?.avatarUrl}
      emoji={child?.avatarEmoji}
      name={child?.label || child?.displayName}
      size={size}
    />
  );
}

// App-styled confirm dialog (reuses the .db-modal scrim) so destructive/important confirmations
// feel native instead of a browser window.confirm. Renders nothing unless `open`.
export function ConfirmDialog({ open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', onConfirm, onCancel, busy = false }) {
  if (!open) return null;
  // Portal to <body> so the fixed modal escapes any ancestor panel's stacking context (.db-panel's
  // backdrop-filter would otherwise trap it — mispositioned + behind later panels).
  return createPortal(
    <div className="db-modal-scrim" onClick={busy ? undefined : onCancel}>
      <div className="db-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 10px', fontSize: 20 }}>{title}</h2>
        {message && <p style={{ margin: '0 0 20px', color: 'var(--muted)', lineHeight: 1.55 }}>{message}</p>}
        <div className="db-row" style={{ gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="db-btn" type="button" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button className="db-btn db-btn-primary" type="button" onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// The viewer's relationship to a member — ICON ONLY, no word (matches the app's MemberConnection):
// green filled overlapping rings = verified friend, grey overlapping rings = friend (not verified
// in person), two non-touching grey rings = someone you've met but aren't connected to. Nothing
// for yourself. The meaning is on hover (title).
export function ConnRings({ connected, verified, isSelf }) {
  if (isSelf) return null;
  const color = verified ? '#22c55e' : '#94a3b8';
  const label = verified ? 'Verified friend' : (connected ? 'Friend' : 'Met');
  const c2 = connected ? 13 : 18; // overlapping (touch) vs spaced (gap)
  const w = c2 + 7;
  const fill = verified ? color : 'none';
  const fillOpacity = verified ? 0.35 : 0;
  return (
    <span title={label} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <svg width={w} height="14" viewBox={`0 0 ${w} 14`} aria-hidden="true">
        <circle cx="7" cy="7" r="5" fill={fill} fillOpacity={fillOpacity} stroke={color} strokeWidth="1.4" />
        <circle cx={c2} cy="7" r="5" fill={fill} fillOpacity={fillOpacity} stroke={color} strokeWidth="1.4" />
      </svg>
    </span>
  );
}

// Shown on a live moment when the viewer has unanswered questions for it — a "?" reminder.
export function QuestionIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="#6366f1" aria-hidden="true"
      style={{ verticalAlign: '-2px' }}>
      <title>Questions to answer</title>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
    </svg>
  );
}
